import { MapPin } from "lucide-react";

import type { Aar, AarIncident } from "../../api/types";
import { motion } from "../../components/presence";
import { Badge, SectionLabel } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { locale, t } from "../../i18n/i18n";
import { plural } from "../../plural";

const fmt = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : v.toLocaleString(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** 3A regroupée par annotation de la carte (docs/08 §19) : pour chaque événement posé sur la carte, les incidents
 *  qu'il a proposés et que l'animateur a retenus, leur cible, leur avancement et leur note. Mêmes incidents que les
 *  fiches par unité, chacun une fois : aucun total n'est compté deux fois. Les incidents dont l'annotation a été
 *  retirée de la carte forment le dernier groupe, « Sans annotation ». Un code ouvre la notation de l'incident. */
export function AnnotationGroups({ data, onRate }: { data: Aar; onRate: (incident: AarIncident) => void }) {
  const groups = data.annotations ?? [];
  if (groups.length === 0) return null;
  const incidents = new Map(data.targets.flatMap((target) => target.incidents).map((i) => [i.id, i]));
  return (
    <section className={`rounded-xl border border-line bg-surface shadow-card ${motion("fade")}`}>
      <div className="flex items-center gap-2 border-b border-line px-5 py-2.5">
        <MapPin size={16} className="shrink-0 text-accent" />
        <SectionLabel as="h2">{t("Par annotation de la carte")}</SectionLabel>
      </div>
      <p className="px-5 pt-2 text-meta leading-snug text-ink-muted">
        {t("Incidents proposés par les événements posés sur la carte et retenus dans la DIREX ; ils sont aussi notés sur la fiche de leur unité, et comptés une seule fois.")}
      </p>
      <ul className="divide-y divide-line px-3 py-1.5">
        {groups.map((group) => (
          <li key={group.id ?? "orphan"} className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1.5 px-2 py-1.5">
            <span className="flex min-w-0 flex-1 basis-56 flex-col">
              <span className="text-ui font-medium text-ink">{group.orphan ? t("Sans annotation") : group.label}</span>
              <span className="text-meta text-ink-muted">
                {group.orphan ? t("Annotation retirée de la carte : les incidents retenus restent.") : group.type_label}
              </span>
            </span>
            <span className="flex flex-wrap gap-1">
              {group.incidents.map((row) => {
                const incident = incidents.get(row.id);
                return (
                  <button
                    key={row.id}
                    type="button"
                    disabled={!incident}
                    onClick={() => incident && onRate(incident)}
                    title={t("{code} — {title} ({unit})", { code: row.code, title: row.short, unit: unitDesignation(row.target_label) })}
                    className="rounded-md border border-line px-1.5 py-0.5 font-mono text-meta text-ink transition-colors hover:bg-hover disabled:opacity-40"
                  >
                    {row.code}
                  </button>
                );
              })}
            </span>
            <Badge tone={group.stats.rated === group.stats.total ? "ok" : "neutral"}>
              {t("{rated}/{total} notés", { rated: group.stats.rated, total: group.stats.total })}
            </Badge>
            <span className="w-12 text-right text-meta font-semibold text-ink tabular-nums" title={t("Note moyenne")}>
              {fmt(group.stats.overall)}
            </span>
          </li>
        ))}
      </ul>
      <p className="px-5 pb-2.5 text-meta text-ink-muted">
        {plural(
          groups.reduce((n, g) => n + g.incidents.length, 0),
          t("incident rattaché à une annotation"),
          t("incidents rattachés à une annotation"),
        )}
        {" · "}
        {t("{total} au total de la 3A", { total: data.totals.total })}
      </p>
    </section>
  );
}
